import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { filter, includes, lowerCase } from 'lodash';
import { useEffect, useState, useRef } from 'react';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import Card from 'components/Layout/Card';
import Button from 'components/form/Button';
import SeachInput from 'components/form/SeachInput';
import Loader from 'components/Layout/Loader';
import Profile from 'components/Layout/Profile';
import FilterDropdown from 'components/Layout/FilterDropdown';
import { addMyInterestSessions, fetchUserEventDetails } from 'store/slices';
import {
  downloadFile,
  formatDate,
  formatSessionTime,
  generatePreSignedUrl,
  getDataFromLocalStorage,
  getEventDate,
  objectToFormData,
  titleCaseString,
} from 'utils/helpers';
import SessionForm from './SessionForm';
import { agendaPath, profilePath } from 'utils/constants';
import './Agenda.scss';
import { icons } from 'utils/constants';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import PanelDetailsPopup from './PanelDetailsPopup/PanelDetailsPopup';

const Agenda = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { memberType, moduleType, eventId } = params;
  const { agendaList, speakerList, eventData, userDetails } = useSelector(
    (state) => ({
      eventData: state.global.eventData,
      agendaList: state.global.eventData?.event_agendas || [],
      speakerList: state.global.eventData?.speaker_details || [],
      userDetails: state.student.userDetails,
    })
  );

  const { user_type } = userDetails;
  const [activeTab, setActiveTab] = useState('all');
  const [isLoading, setLoading] = useState(true);
  const [filterData, setFilterData] = useState({
    searchText: '',
    date: 'all',
    times: '',
  });
  const [sessionId, setSessionId] = useState('');
  const [displayList, setDisplayList] = useState([]);

  const [openPanelDetailsPopup, setOpenPanelDetailsPopup] = useState(false);
  const [selectedId, setSelectedId] = useState('');

  // Saved
  const sessionRef = useRef(null);

  const [speakerPhotos, setSpeakerPhotos] = useState({});
  const [presenterPhotos, setPresenterPhotos] = useState({});

  const filterDays = (type) => {
    setFilterData({ ...filterData, date: type });
    filterDisplayList({ ...filterData, date: type });
  };
  const handelChangeFilter = (e) => {
    setFilterData({ ...filterData, times: e });
    filterDisplayList({ ...filterData, times: e });
  };
  const filterDisplayList = (filterFlags) => {
    const dropFilter = filterFlags.times.split(',');
    let newList = [];
    agendaList.forEach((e) => {
      let sessionArray = [];
      if (e.agenda_sessions.length > 0) {
        e.agenda_sessions.forEach((y) => {
          let keyVal = `${moment(y.date).format('DD MMM')} - ${moment(
            y.session_time,
            'hh:mm:ss'
          ).format('hh:mm A')}`;
          if (y.date === filterFlags.date || filterFlags.date === 'all') {
            if (
              (filterFlags.times && dropFilter.includes(keyVal)) ||
              !filterFlags.times
            ) {
              if (
                (filterFlags.searchText &&
                  lowerCase(y.name).includes(
                    lowerCase(filterFlags.searchText)
                  )) ||
                !filterFlags.searchText
              ) {
                sessionArray.push(y);
              }
            }
          }
        });
      }
      let childObject = { ...e, agenda_sessions: sessionArray };
      newList.push(childObject);
    });
    setDisplayList(newList);
    setLoading(false);
  };

  const handelAddEditMyInterest = async (aIndex, sIndex) => {
    const oldData = cloneDeep(displayList[aIndex].agenda_sessions[sIndex]);
    const response = await dispatch(
      addMyInterestSessions(
        objectToFormData({
          user_id: getDataFromLocalStorage('id'),
          session_id: oldData.id,
        })
      )
    );
    if (response?.status === 200) {
      const oldDisplayArray = cloneDeep(displayList);
      oldDisplayArray[aIndex].agenda_sessions[sIndex].is_interested =
        oldData.is_interested === 0 ? 1 : 0;
      setDisplayList(oldDisplayArray);
      dispatch(
        fetchUserEventDetails({
          event_id: eventId,
          user_id: getDataFromLocalStorage('id'),
        })
      );
    }
  };

  let filterOptions = [];
  agendaList.forEach((e) => {
    e?.agenda_sessions.forEach((y) => {
      if (y.date === filterData.date || filterData.date === 'all') {
        let keyVal = `${moment(y.date).format('DD MMM')} - ${moment(
          y.session_time,
          'hh:mm:ss'
        ).format('hh:mm A')}`;
        filterOptions.push({
          id: keyVal,
          name: keyVal,
        });
      }
    });
  });

  useEffect(() => {
    setDisplayList(agendaList);
    filterDisplayList(filterData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendaList]);

  let count = 0;
  let myInterestCount = 0;
  displayList.forEach((e) => {
    e?.agenda_sessions.forEach((y) => {
      if (y.is_interested) {
        myInterestCount++;
      }
      count++;
    });
  });

  const { virtual_platform_link } = eventData || {};

  useEffect(() => {
    const fetchAllSpeakerPhotos = async () => {
      const photos = {};
      for (const speaker of speakerList) {
        if (speaker.photo) {
          try {
            const url = await generatePreSignedUrl(speaker.photo, profilePath);
            photos[speaker.id] = url;
          } catch (error) {
            console.error(
              `Error fetching photo for speaker ${speaker.id}:`,
              error
            );
          }
        }
      }
      setSpeakerPhotos(photos);
    };

    if (speakerList.length > 0) {
      fetchAllSpeakerPhotos();
    }
  }, [speakerList]);

  useEffect(() => {
    const fetchAllPresenterPhotos = async () => {
      const urls = {};
      for (const agenda of agendaList) {
        for (const session of agenda.agenda_sessions) {
          if (session.speaker_category === 9 && session.presenter_profile) {
            try {
              const url = await generatePreSignedUrl(
                session.presenter_profile,
                profilePath
              );
              urls[session.id] = url;
            } catch (error) {
              console.error(
                `Error fetching presenter profile for session ${session.id}:`,
                error
              );
            }
          }
        }
      }
      setPresenterPhotos(urls);
    };

    if (agendaList?.length > 0) {
      fetchAllPresenterPhotos();
    }
  }, [agendaList]);

  const [maxVisibleSpeakers, setMaxVisibleSpeakers] = useState(2);

  // Responsive speaker display
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1440) {
        setMaxVisibleSpeakers(6);
      } else if (width >= 768) {
        setMaxVisibleSpeakers(4);
      } else {
        setMaxVisibleSpeakers(1);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Extracted components for better organization:

  const SessionMetaInfo = ({ date, startTime, endTime, location }) => (
    <div>
      <div>
        <img src={icons.calendarEvent} alt="Icon" />
        <span>{formatDate(date)}</span>
      </div>
      |
      <div>
        {startTime ? (
          <>
            <img src={icons.clockEvent} alt="Icon" />
            <span>{formatSessionTime(startTime, endTime)}</span>
          </>
        ) : (
          <span>-</span>
        )}
      </div>
      |
      <div>
        <img src={icons.locationEvent} alt="Icon" />
        <span>{location}</span>
      </div>
    </div>
  );

  const SessionActions = ({ userType, onEdit, onInterest }) => (
    <div className="session-save">
      {!['0', '1', '6'].includes(userType) ? (
        <>
          <button onClick={onInterest}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M17.3667 3.84166C16.941 3.41583 16.4357 3.07803 15.8795 2.84757C15.3232 2.6171 14.7271 2.49847 14.125 2.49847C13.5229 2.49847 12.9268 2.6171 12.3705 2.84757C11.8143 3.07803 11.309 3.41583 10.8833 3.84166L10 4.725L9.11666 3.84166C8.25692 2.98192 7.09086 2.49892 5.875 2.49892C4.65914 2.49892 3.49307 2.98192 2.63333 3.84166C1.77359 4.70141 1.29059 5.86747 1.29059 7.08333C1.29059 8.29919 1.77359 9.46525 2.63333 10.325L10 17.6917L17.3667 10.325C17.7925 9.89937 18.1303 9.39401 18.3608 8.83779C18.5912 8.28158 18.7099 7.6854 18.7099 7.08333C18.7099 6.48126 18.5912 5.88508 18.3608 5.32887C18.1303 4.77265 17.7925 4.26729 17.3667 3.84166Z"
                stroke="#746866"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span>Save Interest</span>
        </>
      ) : (
        <Button
          onClick={onEdit}
          text="Edit"
          btnStyle="light-outline"
          className="h-35 text-14-500 cps-16 cpe-16"
          icon={<i className="bi bi-pencil me-2" />}
          isSquare
        />
      )}
    </div>
  );

  return (
    <>
      {openPanelDetailsPopup && (
        <PanelDetailsPopup
          onHide={() => setOpenPanelDetailsPopup(false)}
          selectedId={selectedId}
        />
      )}
      {isLoading ? (
        <Card className="cpt-80 cpb-80">
          <Loader size="md" />
        </Card>
      ) : (
        <>
          <div className="agenda-single-card fadeInUp">
            <div className="agenda-heading">{eventData?.event_name}</div>
            <div>
              <div className="card-detail">
                <img src={icons.calendarEvent} alt="Icon" />
                <span>
                  {getEventDate(eventData?.start_date, eventData?.end_date)}
                </span>
              </div>
              <div className="card-detail">
                <img src={icons.locationEvent} alt="Icon" />
                <span>
                  {eventData?.city_name}, {eventData?.country_name}
                </span>
              </div>
            </div>
          </div>

          <Card className="cmt-12 cps-16 cpe-16 cpt-24 cpb-24 fadeInUp">
            {sessionId && (
              <SessionForm
                onHide={() => setSessionId('')}
                sessionId={sessionId}
                displayList={displayList}
                fetchData={() => {
                  dispatch(
                    fetchUserEventDetails({
                      event_id: eventId,
                      user_id: getDataFromLocalStorage('id'),
                    })
                  );
                  setLoading(true);
                }}
              />
            )}

            <div className="main-agenda">
              <div className="main-agenda-tab">
                <button
                  className={`btn ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all')}
                >
                  All Sessions
                </button>

                <button
                  className={`btn ${
                    activeTab === 'presenters' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('presenters')}
                >
                  All Presenters
                </button>
                {!['0', '1', '6'].includes(user_type) && (
                  <button
                    className={`btn ${
                      activeTab === 'interest' ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab('interest')}
                  >
                    My Interest
                  </button>
                )}
              </div>

              <div className="agenda-filter">
                <div className="days">
                  <button
                    className={`btn ${
                      filterData.date === 'all' ? 'active' : ''
                    }`}
                    onClick={() => filterDays('all')}
                  >
                    All Days
                  </button>

                  {agendaList?.map(
                    (elem, index) =>
                      elem?.agenda_sessions.length > 0 && (
                        <button
                          onClick={() => filterDays(elem.date)}
                          className={`btn ${
                            filterData.date === elem.date ? 'active' : ''
                          }`}
                          key={index}
                        >
                          {`Day ${index + 1}`}
                        </button>
                      )
                  )}
                </div>

                <div className="search-filter">
                  <SeachInput
                    placeholder="Search"
                    value={filterData.searchText}
                    onChange={(e) => {
                      const newSearchText = titleCaseString(e.target.value);
                      setFilterData((prev) => ({
                        ...prev,
                        searchText: newSearchText,
                      }));
                      filterDisplayList({
                        ...filterData,
                        searchText: newSearchText,
                      });
                    }}
                  />

                  <FilterDropdown
                    label="Select Session"
                    list={filterOptions}
                    handelChangeFilter={handelChangeFilter}
                  />

                  {['0', '1', '6'].includes(user_type) && (
                    <div>
                      <Button
                        onClick={() => setSessionId('+1')}
                        text="+ Add Session"
                        btnStyle="primary-outline"
                        className="h-35 text-14-500"
                        isSquare
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="agenda-session">
                {displayList.map((elem, index) => {
                  const shouldHide =
                    elem.agenda_sessions.length === 0 ||
                    (filterData.date !== 'all' &&
                      elem.date !== filterData.date);

                  if (shouldHide) return null;

                  return (
                    <div key={index}>
                      {elem.agenda_sessions.map((sElem, sIndex) => {
                        if (activeTab === 'interest' && !sElem.is_interested)
                          return null;
                        if (
                          activeTab === 'presenters' &&
                          sElem.speaker_category !== 9
                        )
                          return null;

                        const sessionSpeakerList = speakerList?.filter((o) =>
                              sElem?.speaker_id?.includes(o.id)
                        );
                        const isMultiple = sessionSpeakerList.length > 1;
                        const visibleSpeakers = sessionSpeakerList.slice(
                          0,
                          maxVisibleSpeakers
                        );
                        const hiddenCount =
                          sessionSpeakerList.length - visibleSpeakers.length;
                        const sessionLineClass =
                          elem.agenda_sessions.length === 1
                            ? ''
                            : 'session-line';

                        const renderPresenterImage = () => {
                          const popover = (
                            <Popover id={`popover-presenter-${sElem.id}`}>
                              <Popover.Body
                                style={{ minWidth: '220px', padding: '15px' }}
                              >
                                <div>
                                  <div className="text-16-500-20">
                                    {sElem?.name}
                                  </div>
                                </div>
                              </Popover.Body>
                            </Popover>
                          );

                          return (
                            <OverlayTrigger
                              key={`presenter-${sElem.id}`}
                              trigger={['hover', 'focus']}
                              placement="right"
                              overlay={popover}
                            >
                              <div className="speaker-image-container">
                                <img
                                  src={
                                    presenterPhotos[sElem.id] || icons.userImg
                                  }
                                  alt={sElem?.name}
                                  onError={(e) =>
                                    (e.target.src = icons.userImg)
                                  }
                                  loading="lazy"
                                />
                              </div>
                            </OverlayTrigger>
                          );
                        };

                        const renderSpeakerImage = (speaker) => {
                          const popover = (
                            <Popover id={`popover-${speaker.id}`}>
                              <Popover.Body
                                style={{ minWidth: '220px', padding: '15px' }}
                              >
                                <div>
                                  <div className="text-16-500-20">
                                    {sElem.speaker_category === 9
                                      ? sElem?.name
                                      : speaker?.name}
                                  </div>
                                  <div className="tet-14-300-18 cmt-10">
                                    {speaker.designation}
                                  </div>
                                  <div className="tet-14-300-18 cmt-10">
                                    {speaker.institution}
                                  </div>
                                  <div className="tet-14-300-18 cmt-10">
                                    {speaker.country_name}
                                  </div>
                                </div>
                              </Popover.Body>
                            </Popover>
                          );

                          return (
                            <OverlayTrigger
                              key={speaker.id}
                              trigger={['hover', 'focus']}
                              placement="right"
                              overlay={popover}
                            >
                              <div className="speaker-image-container">
                                <img
                                  src={
                                    sElem.speaker_category === 9
                                      ? presenterPhotos[sElem.id] || icons.userImg
                                      : speakerPhotos[speaker.id] || icons.userImg
                                  }
                                  alt={speaker.name}
                                  onError={(e) =>
                                    (e.target.src = icons.userImg)
                                  }
                                  loading="lazy"
                                />
                              </div>
                            </OverlayTrigger>
                          );
                        };

                        return (
                          <div
                            className={`session-one ${
                              activeTab !== 'interest' ? sessionLineClass : ''
                            } ${sElem.is_interested ? 'saved' : ''}`}
                            ref={sessionRef}
                            key={sIndex}
                          >
                            <div className="session-time">
                              <span className="time-badge">
                                {sElem.session_time
                                  ? moment(
                                      sElem.session_time,
                                      'hh:mm:ss'
                                    ).format('hh:mm A')
                                  : '---------'}
                              </span>
                            </div>

                            {isMultiple ? (
                              <div className="session-multiSpeker-card">
                                <div className="session-img">
                                  {sElem.speaker_category === 9
                                    ? renderPresenterImage()
                                    : visibleSpeakers.map(renderSpeakerImage)}

                                  {hiddenCount > 0 && (
                                    <div
                                      className="session-panelists-img"
                                      onClick={() => {
                                        setOpenPanelDetailsPopup(true);
                                        setSelectedId(sElem.id);
                                      }}
                                    >
                                      +{hiddenCount}
                                      <br /> Panel lists
                                    </div>
                                  )}
                                </div>

                                <div className="session-detail cmt-5">
                                  <p className="session-topic">
                                    {sElem.session_topic}
                                  </p>

                                  <SessionMetaInfo
                                    date={sElem.date}
                                    startTime={sElem.session_time}
                                    endTime={sElem.session_end_time}
                                    location={`${eventData.city_name}, ${eventData.country_name}`}
                                  />

                                  <p className="session-name cmb-3">
                                    {sElem.name}
                                  </p>
                                  <a
                                    onClick={() => {
                                      setOpenPanelDetailsPopup(true);
                                      setSelectedId(sElem.id);
                                    }}
                                  >
                                    View Details{' '}
                                    <i class="bi bi-chevron-right"></i>
                                  </a>
                                </div>

                                <SessionActions
                                  userType={user_type}
                                  onEdit={() => setSessionId(sElem.id)}
                                  onInterest={() =>
                                    handelAddEditMyInterest(index, sIndex)
                                  }
                                />
                              </div>
                            ) : (
                              <div className="session-card">
                                <div className="session-img">
                                  {sElem.speaker_category === 9
                                    ? renderPresenterImage()
                                    : sessionSpeakerList[0] &&
                                      renderSpeakerImage(sessionSpeakerList[0])}
                                  {virtual_platform_link && (
                                    <button
                                      className="btn"
                                      onClick={() =>
                                        window.open(
                                          virtual_platform_link,
                                          '_blank'
                                        )
                                      }
                                    >
                                      Join Session
                                    </button>
                                  )}
                                </div>

                                <div className="session-detail">
                                  <a
                                    href={`/${memberType}/${moduleType}/event-details/${eventId}/session/${sElem.id}`}
                                    className="user-name"
                                  >
                                    {sElem.speaker_category === 9
                                      ? sElem.name
                                      : sessionSpeakerList[0]?.name}
                                  </a>
                                  {sElem?.transcription && (
                                    <p
                                      className="session-view"
                                      onClick={async () => {
                                        const res = await generatePreSignedUrl(
                                          sElem.transcription,
                                          agendaPath
                                        );
                                        dispatch(downloadFile(res));
                                      }}
                                    >
                                      View the Transcription
                                    </p>
                                  )}
                                  <p className="session-topic">
                                    {sElem.session_topic}
                                  </p>

                                  <SessionMetaInfo
                                    date={sElem.date}
                                    startTime={sElem.session_time}
                                    endTime={sElem.session_end_time}
                                    location={`${eventData.city_name}, ${eventData.country_name}`}
                                  />

                                  <p className="session-name">
                                    {sElem.speaker_category === 9
                                      ? sessionSpeakerList[0]?.name
                                      : sElem.name}
                                  </p>

                                  {sElem.speaker_category === 9 && (
                                    <p className="session-name cmt-10">
                                      Room No.:-
                                      <span className="cps-5">
                                        {sElem?.session_room_name
                                          ? sElem?.session_room_name
                                          : '-'}
                                      </span>
                                    </p>
                                  )}
                                </div>

                                <SessionActions
                                  userType={user_type}
                                  onEdit={() => setSessionId(sElem.id)}
                                  onInterest={() =>
                                    handelAddEditMyInterest(index, sIndex)
                                  }
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </>
      )}
    </>
  );
};
export default Agenda;

//Old Code
// import { useDispatch, useSelector } from "react-redux";
// import { useNavigate, useParams } from "react-router-dom";
// import { filter, includes, lowerCase } from "lodash";
// import { useEffect, useState, useRef } from "react";
// import { cloneDeep } from "lodash";
// import moment from "moment";
// import Card from "components/Layout/Card";
// import Button from "components/form/Button";
// import SeachInput from "components/form/SeachInput";
// import Loader from "components/Layout/Loader";
// import Profile from "components/Layout/Profile";
// import FilterDropdown from "components/Layout/FilterDropdown";
// import { addMyInterestSessions } from "store/slices";
// import {
//   downloadFile,
//   generatePreSignedUrl,
//   getDataFromLocalStorage,
//   objectToFormData,
//   titleCaseString,
// } from "utils/helpers";
// import SessionForm from "./SessionForm";
// import { agendaPath } from "utils/constants";
// import "./Agenda.scss";
// import { icons } from "utils/constants";

// const Agenda = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const params = useParams();
//   const { memberType, moduleType, eventId } = params;
//   const { agendaList, speakerList, eventData } = useSelector((state) => ({
//     eventData: state.global.eventData,
//     agendaList: state.global.eventData?.event_agendas || [],
//     speakerList: state.global.eventData?.speaker_details || [],
//   }));
//   const [isMyInterest, setIsMyInterest] = useState(false);
//   const [isLoading, setLoading] = useState(true);
//   const [filterData, setFilterData] = useState({
//     searchText: "",
//     date: "all",
//     times: "",
//   });
//   const [sessionId, setSessionId] = useState("");
//   const [displayList, setDisplayList] = useState([]);

//   const filterDays = (type) => {
//     setFilterData({ ...filterData, date: type });
//     filterDisplayList({ ...filterData, date: type });
//   };
//   const handelChangeFilter = (e) => {
//     setFilterData({ ...filterData, times: e });
//     filterDisplayList({ ...filterData, times: e });
//   };
//   const filterDisplayList = (filterFlags) => {
//     const dropFilter = filterFlags.times.split(",");
//     let newList = [];
//     agendaList.forEach((e) => {
//       let sessionArray = [];
//       if (e.agenda_sessions.length > 0) {
//         e.agenda_sessions.forEach((y) => {
//           let keyVal = `${moment(y.date).format("DD MMM")} - ${moment(
//             y.session_time,
//             "hh:mm:ss"
//           ).format("hh:mm A")}`;
//           if (y.date === filterFlags.date || filterFlags.date === "all") {
//             if (
//               (filterFlags.times && dropFilter.includes(keyVal)) ||
//               !filterFlags.times
//             ) {
//               if (
//                 (filterFlags.searchText &&
//                   lowerCase(y.name).includes(
//                     lowerCase(filterFlags.searchText)
//                   )) ||
//                 !filterFlags.searchText
//               ) {
//                 sessionArray.push(y);
//               }
//             }
//           }
//         });
//       }
//       let childObject = { ...e, agenda_sessions: sessionArray };
//       newList.push(childObject);
//     });
//     setDisplayList(newList);
//     setLoading(false);
//   };
//   const handelAddEditMyInterest = async (aIndex, sIndex) => {
//     const oldData = cloneDeep(displayList[aIndex].agenda_sessions[sIndex]);
//     const response = await dispatch(
//       addMyInterestSessions(
//         objectToFormData({
//           user_id: getDataFromLocalStorage("id"),
//           session_id: oldData.id,
//         })
//       )
//     );
//     if (response?.status === 200) {
//       const oldDisplayArray = cloneDeep(displayList);
//       oldDisplayArray[aIndex].agenda_sessions[sIndex].is_interested =
//         oldData.is_interested === 0 ? 1 : 0;
//       setDisplayList(oldDisplayArray);
//     }
//   };

//   let filterOptions = [];
//   agendaList.forEach((e) => {
//     e?.agenda_sessions.forEach((y) => {
//       if (y.date === filterData.date || filterData.date === "all") {
//         let keyVal = `${moment(y.date).format("DD MMM")} - ${moment(
//           y.session_time,
//           "hh:mm:ss"
//         ).format("hh:mm A")}`;
//         filterOptions.push({
//           id: keyVal,
//           name: keyVal,
//         });
//       }
//     });
//   });

//   useEffect(() => {
//     setDisplayList(agendaList);
//     filterDisplayList(filterData);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [agendaList]);

//   let count = 0;
//   let myInterestCount = 0;
//   displayList.forEach((e) => {
//     e?.agenda_sessions.forEach((y) => {
//       if (y.is_interested) {
//         myInterestCount++;
//       }
//       count++;
//     });
//   });
//   const activeDays = "color-new-car primary-underline";
//   const inactiveDays = "color-black-olive pointer";

//   const userType = getDataFromLocalStorage("user_type");
//   const access = {
//     isAdmin: userType === "0",
//   };
//   const { virtual_platform_link } = eventData || {};

//   // Saved
//   const sessionRef = useRef(null);

//   const handleToggle = (e) => {
//     const cardElement = e.currentTarget.closest('.session-one');
//     if (cardElement) {
//       cardElement.classList.toggle('saved');
//     }
//   };

//   return (
//     <>
//       {isLoading ? (
//         <Card className="cpt-80 cpb-80">
//           <Loader size="md" />
//         </Card>
//       ) : (
//         <Card className="cmt-12 cps-16 cpe-16 cpt-24 cpb-24 fadeInUp">
//           {sessionId && (
//             <SessionForm
//               onHide={() => {
//                 setSessionId("");
//               }}
//               sessionId={sessionId}
//               displayList={displayList}
//             />
//           )}

//           <div className="row">
//             <div className="col-md-8">
//               <div className="row w-100 d-flex align-items-center">
//                 <div className="col-md-9 mb-2">
//                   <SeachInput
//                     placeholder="Search here"
//                     value={filterData.searchText}
//                     onChange={(e) => {
//                       setFilterData({
//                         ...filterData,
//                         searchText: titleCaseString(e.target.value),
//                       });
//                       filterDisplayList({
//                         ...filterData,
//                         searchText: titleCaseString(e.target.value),
//                       });
//                     }}
//                   />
//                 </div>
//                 <div className="col-md-3 mb-2">
//                   <FilterDropdown
//                     label={`${!filterData.times
//                       ? 0
//                       : (filterData.times.match(/,/g) || []).length + 1
//                       } Sessions`}
//                     list={filterOptions}
//                     handelChangeFilter={handelChangeFilter}
//                   />
//                 </div>
//               </div>
//               <div></div>
//               <div></div>
//             </div>
//             {/* {access.isAdmin && ( */}
//             <div className="col-md-4 d-flex align-items-center justify-content-end gap-2">
//               <Button
//                 onClick={() => {
//                   setSessionId("+1");
//                 }}
//                 text="+ Add Session"
//                 btnStyle="primary-outline"
//                 className="h-35 text-14-500"
//                 isSquare
//               />
//             </div>
//             {/* )} */}
//           </div>
//           <div className="d-flex justify-content-between align-items-end cmt-24">
//             <div className="d-flex align-items-end">
//               <div
//                 className={`text-16-400 me-3 ${filterData.date === "all" ? activeDays : inactiveDays
//                   }`}
//                 onClick={() => {
//                   filterDays("all");
//                 }}
//               >
//                 All Days
//               </div>
//               {agendaList.map((elem, index) => {
//                 return (
//                   <div
//                     onClick={() => {
//                       filterDays(elem?.date);
//                     }}
//                     className={`text-16-400 me-3 ${elem?.agenda_sessions.length > 0 ? "" : "d-none"
//                       } ${filterData.date === elem?.date ? activeDays : inactiveDays
//                       }`}
//                     key={index}
//                   >{`Day ${index + 1}`}</div>
//                 );
//               })}
//             </div>
//             <div className="text-14-400 d-flex gap-4 cmt-12">
//               <div>
//                 <div
//                   className={
//                     isMyInterest
//                       ? "color-black-olive pointer"
//                       : "color-new-car primary-underline"
//                   }
//                   onClick={() => {
//                     if (isMyInterest) {
//                       setIsMyInterest(false);
//                     }
//                   }}
//                 >
//                   <i className="bi bi-journal-bookmark-fill me-2" />
//                   All Sessions ({count})
//                 </div>
//               </div>

//               {!access.isAdmin && (
//                 <div
//                   className={
//                     isMyInterest
//                       ? "color-new-car primary-underline"
//                       : "color-black-olive pointer"
//                   }
//                   onClick={() => {
//                     if (!isMyInterest) {
//                       setIsMyInterest(true);
//                     }
//                   }}
//                 >
//                   <i className="bi bi-star me-2" />
//                   My Interests ({myInterestCount})
//                 </div>
//               )}
//             </div>
//           </div>
//           {displayList.map((elem, index) => {
//             const hideElement =
//               elem?.agenda_sessions.length > 0
//                 ? elem.date === filterData.date || filterData.date === "all"
//                   ? ""
//                   : "d-none"
//                 : "d-none";
//             return (
//               <div key={index} className={hideElement}>
//                 <div className="cmt-22 cmb-12">
//                   <span className="color-black-olive text-14-400 me-2">
//                     {moment(elem?.date).format("ddd")}
//                   </span>
//                   <span className="color-black-olive text-14-500">
//                     {moment(elem?.date).format("MMM DD")}
//                   </span>
//                 </div>
//                 <div>
//                   {elem?.agenda_sessions.map((sElem, sIndex) => {
//                     const { is_interested } = sElem;
//                     const sessionSpeakerList = filter(speakerList, (o) =>
//                       includes(sElem?.speaker_id, o.id)
//                     );
//                     return (
//                       <div
//                         className={`aganda-session-container cmb-24 ${isMyInterest ? (is_interested ? "" : "d-none") : ""
//                           }`}
//                         key={sIndex}
//                       >
//                         <div className="left-block mt-2">
//                           {moment(sElem.session_time, "hh:mm:ss").format(
//                             "hh:mm A"
//                           )}
//                         </div>
//                         <div className="right-block">
//                           {!access.isAdmin && (
//                             <div
//                               className="star-block pointer"
//                               onClick={() => {
//                                 handelAddEditMyInterest(index, sIndex);
//                               }}
//                             >
//                               <i
//                                 className={
//                                   is_interested
//                                     ? "bi bi-star-fill"
//                                     : "bi bi-star"
//                                 }
//                               />
//                             </div>
//                           )}
//                           <div className="d-flex justify-content-between align-items-center flex-wrap">
//                             <div
//                               className="text-16-500 color-raisin-black pointer mt-2"
//                               onClick={() => {
//                                 navigate(
//                                   `/${memberType}/${moduleType}/event-details/${eventId}/session/${sElem.id}`
//                                 );
//                               }}
//                             >
//                               {sElem?.name}
//                             </div>
//                             {access.isAdmin && (
//                               <Button
//                                 onClick={() => {
//                                   setSessionId(sElem.id);
//                                 }}
//                                 text="Edit"
//                                 btnStyle="light-outline"
//                                 className="h-35 text-14-500 cps-16 cpe-16"
//                                 icon={<i className="bi bi-pencil me-2" />}
//                                 isSquare
//                               />
//                             )}
//                           </div>

//                           {virtual_platform_link && (
//                             <div className="d-flex cmt-12 cmb-22">
//                               <Button
//                                 icon={
//                                   <i className="bi bi-camera-video me-3 text-24-500" />
//                                 }
//                                 text="Join Session"
//                                 btnStyle="primary-dark"
//                                 className="cps-20 cpe-20"
//                                 onClick={() => {
//                                   window.open(virtual_platform_link, "_blank");
//                                 }}
//                               />
//                             </div>
//                           )}
//                           <div className="color-black-olive text-14-400">
//                             {sElem?.description}
//                           </div>
//                           <div
//                             className="text-14-400 color-new-car pointer cmt-16 cmb-22"
//                             onClick={async () => {
//                               const res = await generatePreSignedUrl(
//                                 sElem?.transcription,
//                                 agendaPath
//                               );
//                               dispatch(downloadFile(res));
//                             }}
//                           >
//                             Please click here to view the transcription
//                           </div>
//                           <div className="row">
//                             {sessionSpeakerList.map(
//                               (spreakerElem, speakerIndex) => {
//                                 return (
//                                   <div
//                                     className="col-md-4 mb-2 d-flex pointer"
//                                     key={speakerIndex}
//                                     onClick={() => {
//                                       if (spreakerElem?.name) {
//                                         navigate(
//                                           `/${memberType}/${moduleType}/speaker/${spreakerElem?.id}`
//                                         );
//                                       }
//                                     }}
//                                   >
//                                     <div className="me-2">
//                                       <Profile
//                                         isRounded
//                                         isS3UserURL
//                                         text={
//                                           spreakerElem?.name ||
//                                           spreakerElem?.new_user_name
//                                         }
//                                         url={spreakerElem?.photo}
//                                         size="s-68"
//                                       />
//                                     </div>
//                                     <div>
//                                       <div
//                                         className={`text-15-500 ${spreakerElem?.name
//                                           ? "color-new-car pointer"
//                                           : "color-black-olive"
//                                           }`}
//                                       >
//                                         {spreakerElem?.name ||
//                                           spreakerElem?.new_user_name}
//                                       </div>
//                                       <div className="text-13-400 color-black-olive mt-1 mb-1">
//                                         {spreakerElem?.designation}
//                                       </div>
//                                       <div className="text-13-400 color-black-olive">
//                                         {spreakerElem?.institution}
//                                       </div>
//                                     </div>
//                                   </div>
//                                 );
//                               }
//                             )}
//                           </div>
//                         </div>
//                       </div>
//                     );
//                   })}
//                 </div>
//               </div>
//             );
//           })}
//         </Card>
//       )}
//       <div className="agenda-single-card">
//         <a href="#">International Conference on Language Teaching and Learning (ICLTL-2024)</a>
//         <div>
//           <div className="card-detail">
//             <img src={icons.locationEvent} alt="Icon" />
//             <span>Gujarat, India</span>
//           </div>
//           <div className="card-detail">
//             <img src={icons.calendarEvent} alt="Icon" />
//             <span>26 & 27-July-2024</span>
//           </div>
//         </div>
//       </div>

//       <div className="main-agenda">
//         <div className="main-agenda-tab">
//           <button className="btn active">All Sessions</button>
//           <button className="btn">My Interest</button>
//         </div>
//         <div className="agenda-filter">
//           <div className="days">
//             <button className="btn active">All Days</button>
//             <button className="btn">Day 1</button>
//             <button className="btn">Day 2</button>
//           </div>
//           <div className="search-filter">
//             <SeachInput
//               placeholder="Search"
//               value={filterData.searchText}
//               onChange={(e) => {
//                 setFilterData({
//                   ...filterData,
//                   searchText: titleCaseString(e.target.value),
//                 });
//                 filterDisplayList({
//                   ...filterData,
//                   searchText: titleCaseString(e.target.value),
//                 });
//               }}
//             />
//             <FilterDropdown
//               label={`Select Session`}
//               list={filterOptions}
//               handelChangeFilter={handelChangeFilter}
//             />
//             <button className="btn btn-search"><img src={icons.searchLight} alt="Search" /></button>
//           </div>
//         </div>
//         <div className="agenda-session">
//           <div className="session-one session-line" ref={sessionRef}>
//             <div className="session-time">
//               <span className="time-badge">10:00am</span>
//             </div>
//             <div className="session-card">
//               <div className="session-img">
//                 <img src={icons.userImg} alt="Image" />
//                 <button className="btn">Join Session</button>
//               </div>
//               <div className="session-detail">
//                 <a href="#" className="user-name">Dr Kalari Srikanth</a>
//                 <a href="#" className="session-view">View the Transcription</a>
//                 <p className="session-topic">Session Topic Lorel ipsum Lorel ipsum Lorel ipsum Lorel ipsum</p>
//                 <div>
//                   <div>
//                     <img src={icons.calendarEvent} alt="Icon" />
//                     <span>26 & 27-July-2024</span>
//                   </div>|
//                   <div>
//                     <img src={icons.clockEvent} alt="Icon" />
//                     <span>10am - 11:30am</span>
//                   </div>|
//                   <div>
//                     <img src={icons.locationEvent} alt="Icon" />
//                     <span>1st floor, Marina Hall, Singapore</span>
//                   </div>
//                 </div>
//                 <p className="session-name">Session Name Lorel ipsum Lorel ipsum Lorel ipsum Lorel ipsum</p>
//               </div>
//               <div className="session-save">
//                 <button onClick={handleToggle}>
//                   <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
//                     <path d="M17.3667 3.84166C16.941 3.41583 16.4357 3.07803 15.8795 2.84757C15.3232 2.6171 14.7271 2.49847 14.125 2.49847C13.5229 2.49847 12.9268 2.6171 12.3705 2.84757C11.8143 3.07803 11.309 3.41583 10.8833 3.84166L10 4.725L9.11666 3.84166C8.25692 2.98192 7.09086 2.49892 5.875 2.49892C4.65914 2.49892 3.49307 2.98192 2.63333 3.84166C1.77359 4.70141 1.29059 5.86747 1.29059 7.08333C1.29059 8.29919 1.77359 9.46525 2.63333 10.325L10 17.6917L17.3667 10.325C17.7925 9.89937 18.1303 9.39401 18.3608 8.83779C18.5912 8.28158 18.7099 7.6854 18.7099 7.08333C18.7099 6.48126 18.5912 5.88508 18.3608 5.32887C18.1303 4.77265 17.7925 4.26729 17.3667 3.84166Z" stroke="#746866" stroke-linecap="round" stroke-linejoin="round" />
//                   </svg>
//                 </button>
//                 <span>Save Interest</span>
//               </div>
//             </div>
//           </div>
//           <div className="session-one session-line" ref={sessionRef}>
//             <div className="session-time">
//               <span className="time-badge">10:00am</span>
//             </div>
//             <div className="session-card">
//               <div className="session-img">
//                 <img src={icons.userImg} alt="Image" />
//                 <button className="btn">Join Session</button>
//               </div>
//               <div className="session-detail">
//                 <a href="#" className="user-name">Dr Kalari Srikanth</a>
//                 <a href="#" className="session-view">View the Transcription</a>
//                 <p className="session-topic">Session Topic Lorel ipsum Lorel ipsum Lorel ipsum Lorel ipsum</p>
//                 <div>
//                   <div>
//                     <img src={icons.calendarEvent} alt="Icon" />
//                     <span>26 & 27-July-2024</span>
//                   </div>|
//                   <div>
//                     <img src={icons.clockEvent} alt="Icon" />
//                     <span>10am - 11:30am</span>
//                   </div>|
//                   <div>
//                     <img src={icons.locationEvent} alt="Icon" />
//                     <span>1st floor, Marina Hall, Singapore</span>
//                   </div>
//                 </div>
//                 <p className="session-name">Session Name Lorel ipsum Lorel ipsum Lorel ipsum Lorel ipsum</p>
//               </div>
//               <div className="session-save">
//                 <button onClick={handleToggle}>
//                   <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
//                     <path d="M17.3667 3.84166C16.941 3.41583 16.4357 3.07803 15.8795 2.84757C15.3232 2.6171 14.7271 2.49847 14.125 2.49847C13.5229 2.49847 12.9268 2.6171 12.3705 2.84757C11.8143 3.07803 11.309 3.41583 10.8833 3.84166L10 4.725L9.11666 3.84166C8.25692 2.98192 7.09086 2.49892 5.875 2.49892C4.65914 2.49892 3.49307 2.98192 2.63333 3.84166C1.77359 4.70141 1.29059 5.86747 1.29059 7.08333C1.29059 8.29919 1.77359 9.46525 2.63333 10.325L10 17.6917L17.3667 10.325C17.7925 9.89937 18.1303 9.39401 18.3608 8.83779C18.5912 8.28158 18.7099 7.6854 18.7099 7.08333C18.7099 6.48126 18.5912 5.88508 18.3608 5.32887C18.1303 4.77265 17.7925 4.26729 17.3667 3.84166Z" stroke="#746866" stroke-linecap="round" stroke-linejoin="round" />
//                   </svg>
//                 </button>
//                 <span>Save Interest</span>
//               </div>
//             </div>
//           </div>
//           <div className="session-one session-line" ref={sessionRef}>
//             <div className="session-time">
//               <span className="time-badge">10:00am</span>
//             </div>
//             <div className="session-card">
//               <div className="session-img">
//                 <img src={icons.userImg} alt="Image" />
//                 <button className="btn">Join Session</button>
//               </div>
//               <div className="session-detail">
//                 <a href="#" className="user-name">Dr Kalari Srikanth</a>
//                 <a href="#" className="session-view">View the Transcription</a>
//                 <p className="session-topic">Session Topic Lorel ipsum Lorel ipsum Lorel ipsum Lorel ipsum</p>
//                 <div>
//                   <div>
//                     <img src={icons.calendarEvent} alt="Icon" />
//                     <span>26 & 27-July-2024</span>
//                   </div>|
//                   <div>
//                     <img src={icons.clockEvent} alt="Icon" />
//                     <span>10am - 11:30am</span>
//                   </div>|
//                   <div>
//                     <img src={icons.locationEvent} alt="Icon" />
//                     <span>1st floor, Marina Hall, Singapore</span>
//                   </div>
//                 </div>
//                 <p className="session-name">Session Name Lorel ipsum Lorel ipsum Lorel ipsum Lorel ipsum</p>
//               </div>
//               <div className="session-save">
//                 <button onClick={handleToggle}>
//                   <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
//                     <path d="M17.3667 3.84166C16.941 3.41583 16.4357 3.07803 15.8795 2.84757C15.3232 2.6171 14.7271 2.49847 14.125 2.49847C13.5229 2.49847 12.9268 2.6171 12.3705 2.84757C11.8143 3.07803 11.309 3.41583 10.8833 3.84166L10 4.725L9.11666 3.84166C8.25692 2.98192 7.09086 2.49892 5.875 2.49892C4.65914 2.49892 3.49307 2.98192 2.63333 3.84166C1.77359 4.70141 1.29059 5.86747 1.29059 7.08333C1.29059 8.29919 1.77359 9.46525 2.63333 10.325L10 17.6917L17.3667 10.325C17.7925 9.89937 18.1303 9.39401 18.3608 8.83779C18.5912 8.28158 18.7099 7.6854 18.7099 7.08333C18.7099 6.48126 18.5912 5.88508 18.3608 5.32887C18.1303 4.77265 17.7925 4.26729 17.3667 3.84166Z" stroke="#746866" stroke-linecap="round" stroke-linejoin="round" />
//                   </svg>
//                 </button>
//                 <span>Save Interest</span>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };
// export default Agenda;
